import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  MessageSquare, Phone, CheckCircle2, Circle, Lock, Edit3, History,
  Loader2, Save, AlertTriangle, RefreshCw, Eye
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPromptVersions({ authToken }) {
  const [versions, setVersions] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // { version, content }
  const [previewModal, setPreviewModal] = useState(null); // version object
  const [activateConfirm, setActivateConfirm] = useState(null); // version object
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [vRes, hRes] = await Promise.all([
        axios.get(`${API}/admin/prompt-versions`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/admin/prompt-versions/log/history?limit=30`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      setVersions(vRes.data || []);
      setHistory(hRes.data || []);
    } catch (e) {
      toast.error("Fehler beim Laden: " + (e.response?.data?.detail || e.message));
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (v) => setEditModal({ version: v, content: { ...v.content } });

  const saveEdit = async () => {
    if (!editModal) return;
    setIsSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/prompt-versions/${editModal.version.id}`,
        { content: editModal.content },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Änderungen gespeichert ✅");
      if (res.data.sync_result) {
        toast.success("Voice-Bot bei ElevenLabs aktualisiert 📞");
      }
      setEditModal(null);
      load();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    } finally {
      setIsSaving(false);
    }
  };

  const doActivate = async () => {
    if (!activateConfirm) return;
    setIsActivating(true);
    try {
      const res = await axios.post(
        `${API}/admin/prompt-versions/${activateConfirm.id}/activate`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`Version ${activateConfirm.version} "${activateConfirm.name}" ist jetzt AKTIV 🟢`);
      if (activateConfirm.bot === "voice") {
        const sync = res.data.sync_result || {};
        const inOk = sync.inbound?.success;
        const outOk = sync.outbound?.success;
        if (inOk && outOk) toast.success("ElevenLabs erfolgreich gepatcht (Inbound + Outbound) 📞");
        else if (!inOk || !outOk) toast.warning("ElevenLabs teilweise gepatcht – siehe Historie");
      }
      setActivateConfirm(null);
      load();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    } finally {
      setIsActivating(false);
    }
  };

  const resyncSnapshot = async () => {
    try {
      await axios.post(
        `${API}/admin/prompt-versions/seed/reset`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Version A (Voice) wurde vom Live-Zustand aktualisiert 🔄");
      load();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  const syncToElevenLabs = async (v) => {
    try {
      const res = await axios.post(
        `${API}/admin/prompt-versions/${v.id}/sync`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const sync = res.data.sync_result || {};
      const inOk = sync.inbound?.success;
      const outOk = sync.outbound?.success;
      if (inOk && outOk) toast.success(`Version ${v.version} zu ElevenLabs gesynct (Inbound + Outbound) 📞`);
      else toast.warning("Sync teilweise erfolgreich – siehe Historie");
      load();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const chatVersions = versions.filter((v) => v.bot === "chat").sort((a, b) => a.version.localeCompare(b.version));
  const voiceVersions = versions.filter((v) => v.bot === "voice").sort((a, b) => a.version.localeCompare(b.version));

  const renderCard = (v) => {
    const isActive = v.active;
    const isEditable = v.editable;
    return (
      <Card
        key={v.id}
        className={`border-2 transition-all ${isActive ? "border-green-500 shadow-lg" : "border-slate-200"}`}
        data-testid={`prompt-version-${v.bot}-${v.version}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {isActive ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-300">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> AKTIV
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-500">
                    <Circle className="w-3 h-3 mr-1" /> Inaktiv
                  </Badge>
                )}
                {!isEditable && (
                  <Badge variant="outline" className="text-slate-500 border-slate-300">
                    <Lock className="w-3 h-3 mr-1" /> Schreibgeschützt
                  </Badge>
                )}
              </div>
              <CardTitle className="text-lg">Version {v.version} – {v.name}</CardTitle>
              <CardDescription className="text-xs mt-1">
                Zuletzt geändert: {v.updated_at ? new Date(v.updated_at).toLocaleString("de-DE") : "–"}
                {v.updated_by && ` durch ${v.updated_by}`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-2 flex-wrap">
            {!isActive && (
              <Button
                size="sm"
                onClick={() => setActivateConfirm(v)}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid={`activate-${v.bot}-${v.version}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Aktivieren
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setPreviewModal(v)} data-testid={`preview-${v.bot}-${v.version}`}>
              <Eye className="w-4 h-4 mr-1" /> Vorschau
            </Button>
            {isEditable && (
              <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`edit-${v.bot}-${v.version}`}>
                <Edit3 className="w-4 h-4 mr-1" /> Bearbeiten
              </Button>
            )}
            {v.bot === "voice" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncToElevenLabs(v)}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid={`sync-${v.bot}-${v.version}`}
                title="Aktuellen Inhalt jetzt zu ElevenLabs pushen (auch wenn Version nicht aktiv ist)"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Jetzt zu ElevenLabs syncen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="prompt-versions-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Prompt-Verwaltung (A/B-Toggle)</h1>
        <p className="text-slate-600 text-sm">
          Schalte zwischen der Original-Version (Fallback) und der neuen Master-Lea-Version um.
          Umschaltung wirkt sofort – für Chat live, für Voice-Bot nach 2-3s ElevenLabs-Sync.
        </p>
      </div>

      {/* CHAT SECTION */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-slate-900">💬 Chat-Bot (factoryhype.de/chat)</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {chatVersions.map(renderCard)}
        </div>
      </section>

      {/* VOICE SECTION */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-slate-900">📞 Voice-Bot (Telefon – Inbound + Outbound)</h2>
          </div>
          <Button size="sm" variant="ghost" onClick={resyncSnapshot} data-testid="resync-live">
            <RefreshCw className="w-4 h-4 mr-1" /> Version A vom Live-Stand aktualisieren
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {voiceVersions.map(renderCard)}
        </div>
      </section>

      {/* HISTORY */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <History className="w-5 h-5 text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">Änderungs-Historie</h2>
        </div>
        <Card>
          <CardContent className="pt-4">
            {history.length === 0 ? (
              <p className="text-slate-500 text-sm">Noch keine Umschaltungen protokolliert.</p>
            ) : (
              <ul className="space-y-2" data-testid="history-list">
                {history.map((h) => (
                  <li key={h.id} className="flex items-start justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                    <div>
                      <span className="font-medium text-slate-900">
                        {h.bot === "chat" ? "💬 Chat" : "📞 Voice"}: {h.from_version || "?"} → {h.to_version} ({h.to_version_name})
                      </span>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {new Date(h.activated_at).toLocaleString("de-DE")} durch {h.activated_by}
                      </div>
                    </div>
                    {h.sync_result && (
                      <div className="text-xs text-right">
                        {h.sync_result.inbound?.success ? "✅ In" : "❌ In"} {" "}
                        {h.sync_result.outbound?.success ? "✅ Out" : "❌ Out"}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      {/* EDIT MODAL */}
      <Dialog open={!!editModal} onOpenChange={(o) => !o && setEditModal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {editModal && (
            <>
              <DialogHeader>
                <DialogTitle>Bearbeiten: {editModal.version.name} ({editModal.version.bot === "chat" ? "Chat" : "Voice"})</DialogTitle>
                <DialogDescription>
                  {editModal.version.active
                    ? "⚠️ Diese Version ist AKTIV – Änderungen wirken sofort auf Live-Chats/Anrufe!"
                    : "Änderungen an inaktiven Versionen werden nur gespeichert – nicht live."}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-4 py-2">
                {editModal.version.bot === "chat" ? (
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1 block">Chat System-Prompt</label>
                    <Textarea
                      className="font-mono text-xs h-[60vh]"
                      value={editModal.content?.system_message || ""}
                      onChange={(e) => setEditModal({ ...editModal, content: { ...editModal.content, system_message: e.target.value } })}
                      data-testid="edit-chat-textarea"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-1 block">📞 Inbound-Prompt (eingehende Anrufe)</label>
                      <Textarea
                        className="font-mono text-xs h-[30vh]"
                        value={editModal.content?.inbound || ""}
                        onChange={(e) => setEditModal({ ...editModal, content: { ...editModal.content, inbound: e.target.value } })}
                        data-testid="edit-voice-inbound-textarea"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-1 block">📞 Outbound-Prompt (Rückrufe)</label>
                      <Textarea
                        className="font-mono text-xs h-[30vh]"
                        value={editModal.content?.outbound || ""}
                        onChange={(e) => setEditModal({ ...editModal, content: { ...editModal.content, outbound: e.target.value } })}
                        data-testid="edit-voice-outbound-textarea"
                      />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditModal(null)}>Abbrechen</Button>
                <Button onClick={saveEdit} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-btn">
                  {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PREVIEW MODAL */}
      <Dialog open={!!previewModal} onOpenChange={(o) => !o && setPreviewModal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {previewModal && (
            <>
              <DialogHeader>
                <DialogTitle>Vorschau: {previewModal.name} ({previewModal.bot === "chat" ? "Chat" : "Voice"})</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-2">
                {previewModal.bot === "chat" ? (
                  <pre className="whitespace-pre-wrap text-xs bg-slate-50 p-4 rounded font-mono max-h-[70vh] overflow-y-auto">
                    {previewModal.content?.system_message || "(leer)"}
                  </pre>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold mb-2">📞 Inbound</h3>
                      <pre className="whitespace-pre-wrap text-xs bg-slate-50 p-4 rounded font-mono max-h-[40vh] overflow-y-auto">
                        {previewModal.content?.inbound || "(leer)"}
                      </pre>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold mb-2">📞 Outbound</h3>
                      <pre className="whitespace-pre-wrap text-xs bg-slate-50 p-4 rounded font-mono max-h-[40vh] overflow-y-auto">
                        {previewModal.content?.outbound || "(leer)"}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPreviewModal(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ACTIVATE CONFIRM */}
      <AlertDialog open={!!activateConfirm} onOpenChange={(o) => !o && setActivateConfirm(null)}>
        <AlertDialogContent>
          {activateConfirm && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <AlertTriangle className="w-5 h-5 text-yellow-500 inline mr-2" />
                  Version aktivieren?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Version <strong>{activateConfirm.version} "{activateConfirm.name}"</strong> für{" "}
                  <strong>{activateConfirm.bot === "chat" ? "💬 Chat-Bot" : "📞 Voice-Bot"}</strong> wird aktiviert.
                  <br /><br />
                  {activateConfirm.bot === "chat"
                    ? "→ Wirkt SOFORT auf alle neuen Chat-Sessions."
                    : "→ Wirkt nach ca. 2-3 Sekunden auf alle neuen Anrufe (ElevenLabs Inbound + Outbound werden aktualisiert)."}
                  <br /><br />
                  Rückgängig machen? → einfach die andere Version aktivieren.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isActivating}>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={doActivate}
                  disabled={isActivating}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-activate-btn"
                >
                  {isActivating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Ja, aktivieren
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
